import React, { PropsWithChildren, useCallback, useContext, useEffect, FC } from 'react';
import invariant from 'tiny-invariant';
import { BundleSizeList } from 'src/components/BundleProduct/BundleSizeList';
import { Skeleton } from 'src/components/Common/Skeleton';
import { ProductSwatch } from 'src/components/ProductDetail/ProductSwatch';
import { useConstant } from 'src/core/hooks/react-hooks';
import { Form } from 'src/core/forms';
import {
    BundleSelectionContext,
    BundleSelectionProvider,
    SubmitFn,
} from 'src/components/BundleProduct/BundleSelectionProvider';
import { BehaviorSubject, Subject } from 'rxjs';
import { ProductLocation } from 'src/components/CustomisationPage/feature/customisation.types';
import { FormData } from 'src/components/BundleProduct/utils/formDataToPayload';
import {
    useWearerBundleFormState,
    WearerFormApi,
} from 'src/components/WeareBundleProduct/hooks/useWearerBundleFormState';
import { cheapClone } from 'src/util/general';
import { useLazyRegister } from 'src/core/engine/hooks/useRegisterFeature';
import { BundleMsg, bundleRegister } from 'src/components/BundleProduct/feature/bundle.register';
import { BaseProduct } from 'src/components/ProductDetail/utils/product-detail.types';
import { ProductPrice } from 'src/types/global-types';
import { BundleProductContent } from 'src/components/BundleProduct/BundleProductContent';
import { useWearerBundles } from 'src/hooks/useWearerBundles';
import { BundlePageSkeleton } from 'src/elements/Skeletons/Skeletons';
import { useDispatch } from 'react-redux';
import { convertFormDataToPayload } from 'src/components/BundleProduct/utils/convertFormDataToPayload';
import { WEARER_BUNDLE_MAX_CUSTOMISATIONS } from 'src/constants/customisation';

import {
    BundleOptionsContext,
    WearerSummary,
    WearerBundleOptionsProvider,
} from '../BundleProduct/BundleOptionsProvider';

import { WearerStepSubmitConnected } from './WearerStepSubmit';
import { WearerBundleSummary } from './WearerBundleSummary';
import { WearerOptionMap, WearerOptions } from './utils/wearer.types';
import classes from './WeareBundleProduct.scss';
import { AddLogo } from './WearerAddLogo';

export type BuilderState = {
    step: number;
    max: number;
    isLast: boolean;
    isFirst: boolean;
    id: number;
};

export type Props = {
    wearerOptions: WearerOptionMap;
    summary: WearerSummary[];
    product: BaseProduct;
    locations: ProductLocation[];
    price$: BehaviorSubject<ProductPrice>;
};

export function WearerBundleOptions(props: Props) {
    useLazyRegister(bundleRegister, true, 'BundleSelectionProvider');
    const { reset, state, onSubmit, qtyMap, id, onArtworkSubmit, formState, formData$ } = useWearerBundleFormState(
        props.wearerOptions,
        props.product.sku,
    );

    return (
        <div className={classes.wrapper} data-scroll-target="WearerBundleOptions" data-test-id="WearerBundleOptions">
            <div className={classes.summaryWrapper}>
                <WearerBundleSummary
                    parentId={id}
                    summary={props.summary}
                    qtyMap={qtyMap}
                    current={state.step}
                    formData$={formData$}
                />
            </div>
            <div className={classes.content}>
                {!state.isLast && (
                    <WearerBundleStep
                        wearerOptions={props.wearerOptions}
                        id={id}
                        onSubmit={onSubmit}
                        initialState={formState.items[id]?.raw}
                    >
                        <Inner
                            summary={props.summary}
                            initialValues={formState.items[id]?.raw}
                            state={state}
                            formData$={formData$}
                        />
                    </WearerBundleStep>
                )}
                {state.isLast && (
                    <AddLogo
                        product={props.product}
                        locations={props.locations}
                        onArtworkSubmit={onArtworkSubmit}
                        formState={formState}
                        price$={props.price$}
                        reset={reset}
                    />
                )}
            </div>
        </div>
    );
}

export type StepProps = {
    wearerOptions: WearerOptionMap;
    id: number;
    onSubmit: WearerFormApi['onSubmit'];
    initialState?: Record<string, any>;
};

function WearerBundleStep(props: PropsWithChildren<StepProps>) {
    const { id, onSubmit } = props;
    const options: WearerOptions | undefined = props.wearerOptions.get(id);
    invariant(options, `missing options for id: ${id}`);

    const submit$ = useConstant(() => new Subject());
    const onSubmitLocal = useCallback(
        (values) => {
            submit$.next(values);
        },
        [submit$],
    );

    const onComplete: SubmitFn = useCallback(
        (payload, raw, formData) => {
            onSubmit({
                raw: cheapClone(raw)!,
                atbPayload: cheapClone(payload)!,
                formData: cheapClone(formData)!,
            });
        },
        [onSubmit],
    );

    return (
        <Form
            key={id}
            className={classes.form}
            onSubmit={onSubmitLocal}
            data-test-id="WearerBundleProductPage_Form"
            initialValues={props.initialState}
        >
            <WearerBundleOptionsProvider ids={[id]} bundle_options={options.bundle_options} max_qty={options.max_qty}>
                <BundleSelectionProvider sku={options.sku} id={id} submit$={submit$} onSubmit={onComplete}>
                    {props.children}
                </BundleSelectionProvider>
            </WearerBundleOptionsProvider>
        </Form>
    );
}

export type ModalWeareBundleProps = {
    wearerOptions: WearerOptionMap;
    product: BaseProduct;
    summary: WearerSummary[];
    prices: ProductPrice;
    customisationOption: string;
};

export const ModalWearerBundle: FC<ModalWeareBundleProps> = ({
    wearerOptions,
    product,
    summary,
    prices,
    customisationOption,
}) => {
    useLazyRegister(bundleRegister, true, 'BundleSelectionProvider');
    const dispatch = useDispatch();

    const bundles = useWearerBundles(wearerOptions, summary).array;

    if (!bundles) {
        return <BundlePageSkeleton />;
    }

    const onSubmit = (data) => {
        dispatch(BundleMsg('Bundle.Add', convertFormDataToPayload(data, product.sku, bundles)));
    };

    return (
        <BundleProductContent
            product={product}
            bundles={bundles}
            bundlePrice={prices}
            maxCustomisations={WEARER_BUNDLE_MAX_CUSTOMISATIONS}
            onSubmit={onSubmit}
            customisationOption={customisationOption}
        />
    );
};

export type InnerProps = {
    summary: WearerSummary[];
    initialValues?: Record<string, any>;
    state: BuilderState;
    formData$: Subject<FormData>;
};

function Inner(props: InnerProps) {
    const { loading, swatches, sizes } = useContext(BundleOptionsContext);
    const { formData$ } = useContext(BundleSelectionContext);

    useEffect(() => {
        const sub = formData$.subscribe(props.formData$);
        return () => sub.unsubscribe();
    }, [formData$, props.formData$]);

    if (loading) {
        return <Skeleton />;
    }
    return (
        <div>
            <ProductSwatch
                title="1. Select colour(s)"
                swatches={swatches}
                count={swatches.length}
                isBundleView
                sizes={sizes}
            />
            <BundleSizeList sizes={sizes} swatches={swatches} initialValues={props.initialValues} />
            <WearerStepSubmitConnected state={props.state} />
        </div>
    );
}

export default WearerBundleOptions;
